'use client';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Box, CircularProgress, Typography } from '@mui/material';

import { EmptyState, GamesViewSkeleton } from '@/components';
import { useT } from '@/hooks';
import { EventResponse } from '@/types';
import { GameListByDate } from './GameListByDate';

interface GamesViewProps {
  games: EventResponse[];
  gamesByDate: Record<string, EventResponse[]>;
  sportSlug: string;
  onTeamClick: (event: EventResponse, teamIndex: 1 | 2, isPreviousMarket?: boolean) => void;
  onPropTradeClick?: (marketId: string, outcomeId: string, isBuy: boolean) => void;
  isLoading: boolean;
  columnVisibility: { hasMoneyline: boolean; hasSpread: boolean; hasTotal: boolean };
  activeOutcomeId?: string;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

export const GamesView = ({
  games,
  gamesByDate,
  sportSlug,
  onTeamClick,
  onPropTradeClick,
  isLoading,
  columnVisibility,
  activeOutcomeId,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
}: GamesViewProps) => {
  const { t } = useT();
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && fetchNextPage) {
      fetchNextPage?.();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return <GamesViewSkeleton />;
  }

  if (!games || games.length === 0) {
    return (
      <EmptyState
        title={`No games available for ${sportSlug ? sportSlug.toUpperCase() : ''}`}
        description="Try selecting a different category or check back later."
      />
    );
  }

  const renderGameGroup = ([date, gamesList]: [string, EventResponse[]]) => {
    return (
      <Box key={date} sx={{ width: '100%' }}>
        <GameListByDate
          date={date}
          games={gamesList || []}
          sportSlug={sportSlug || ''}
          onTeamClick={onTeamClick}
          onPropTradeClick={onPropTradeClick}
          columnVisibility={columnVisibility}
          activeOutcomeId={activeOutcomeId}
        />
      </Box>
    );
  };

  const renderedGamesList = Object.entries(gamesByDate).map(renderGameGroup);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, width: '100%' }}>
      {renderedGamesList}

      {/* Infinite Scroll Trigger & Footer */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 4,
          width: '100%',
        }}
      >
        {hasNextPage ? (
          <Box ref={ref} sx={{ display: 'flex', justifyContent: 'center', minHeight: 40 }}>
            {isFetchingNextPage && <CircularProgress size={32} />}
          </Box>
        ) : renderedGamesList.length > 0 ? (
          <Typography color="text.secondary" variant="body2" sx={{ opacity: 0.8 }}>
            {t('sports.noMoreGames', { defaultValue: 'No more games to load' })}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};
